import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import type { PriceTier } from 'utils/price-units';
import type { PriceImportRow, PriceImportRowStatus } from './types';
import { priceImportsEndpointIdentifier } from './use-get-price-import-runs-api';

/**
 * One operator decision. Every field is optional by intent — the review grid
 * patches a single cell without resending the row.
 */
export interface UpdatePriceImportRowBody {
  masterProductId?: number;
  dealPrice?: number;
  dealUnit?: PriceTier;
  status?: Extract<PriceImportRowStatus, 'approved' | 'skipped' | 'rejected' | 'needs_review'>;
  notes?: string;
  approveProposal?: boolean;
}

type TData = { data: PriceImportRow };
type TError = AxiosError;
type TVariables = { runId: number; rowId: number; body: UpdatePriceImportRowBody };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useUpdatePriceImportRowApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ runId, rowId, body }: TVariables) => {
    try {
      const { data } = await http.put(`${priceImportsEndpointIdentifier}/${runId}/rows/${rowId}`, body);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${priceImportsEndpointIdentifier}/rows`],
    // A flaky network must not double-apply a decision.
    retry: false,
    ...options,
  });
}
